import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useLearnerAuth } from "@/contexts/LearnerAuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import {
  LayoutDashboard,
  BookOpen,
  Link2,
  BarChart3,
  LogOut,
  Menu,
  X,
  Monitor,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BannerHost from "@/components/BannerHost";

const navItems = [
  { label: "Dashboard", path: "/portal/dashboard", icon: LayoutDashboard },
  { label: "My Results", path: "/portal/results", icon: BarChart3 },
  { label: "Learning Experiences", path: "/portal/learning", icon: BookOpen },
  { label: "Resources", path: "/portal/resources", icon: Link2 },
  { label: "Sandbox", path: "/portal/epic-practice", icon: Monitor, requireFeature: "epic_practice" },
];

function getSectionTitle(pathname, items) {
  const item = items.find((i) => pathname.startsWith(i.path));
  return item ? item.label : "Dashboard";
}

export default function LearnerLayout() {
  const { learner, logout } = useLearnerAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const firstName = learner?.first_name || "Learner";
  const fullName = `${learner?.first_name || ""} ${learner?.last_name || ""}`.trim();

  // Filter by org feature flags (same pattern as AdminLayout). If a flag is
  // explicitly `false`, hide the nav entry; missing/undefined = allow.
  // Also honour per-user `hidden_features` (SA-managed cosmetic hide).
  const featureFlags = learner?.feature_flags || {};
  const hiddenFeatures = new Set(learner?.hidden_features || []);
  const epicLabel = (learner?.epic_practice_label || "").trim();
  const visibleNav = navItems
    .filter((it) => !it.requireFeature || featureFlags[it.requireFeature] !== false)
    .filter((it) => !it.requireFeature || !hiddenFeatures.has(it.requireFeature))
    .map((it) => {
      if (it.path === "/portal/epic-practice" && epicLabel) {
        return { ...it, label: epicLabel };
      }
      return it;
    });
  const sectionTitle = getSectionTitle(location.pathname, visibleNav);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--user-bg, #F8F9FC)" }} data-testid="learner-layout">
      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 z-40"
        style={{
          backgroundColor: "var(--user-sidebar-bg, #FFFFFF)",
          backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))",
          borderRight: "1px solid var(--user-sidebar-border)",
        }}
        data-testid="learner-sidebar"
      >
        <div className="h-20 flex flex-col justify-center px-5 shrink-0" style={{ borderBottom: "1px solid var(--user-sidebar-border)" }} data-testid="learner-sidebar-brand">
          <h1 className="font-heading text-lg font-bold tracking-tight leading-tight truncate" style={{ color: "var(--user-sidebar-text)" }} data-testid="learner-sidebar-org-name">
            {learner?.org_name || "Organization"}
          </h1>
          <span className="text-[10px] font-body uppercase tracking-wider mt-0.5" style={{ color: "var(--user-sidebar-muted)", opacity: 0.7 }} data-testid="learner-sidebar-powered-by">
            Powered by DiNAXLP
          </span>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`learner-nav-${item.path.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                  isActive ? "font-body font-bold" : "font-body font-medium"
                }`
              }
              style={({ isActive }) => ({
                color: "var(--shell-text)",
                opacity: isActive ? 1 : 0.65,
                borderLeft: isActive ? "3px solid var(--shell-section-label)" : "3px solid transparent",
                paddingLeft: isActive ? "calc(0.75rem - 3px)" : "0.75rem",
                paddingRight: "0.75rem",
                backgroundColor: "transparent",
              })}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 shrink-0" style={{ borderTop: "1px solid var(--user-sidebar-border)" }}>
          <div className="flex items-center gap-3 px-3 py-1.5">
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {firstName[0]}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-body font-medium truncate" style={{ color: "var(--user-sidebar-text)" }} data-testid="learner-sidebar-name">
                {fullName}
              </p>
              <p className="text-xs font-body" style={{ color: "var(--user-sidebar-muted)" }}>
                {learner?.cohort_name || settings.learner_label}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen overflow-x-hidden">
        {/* Top Nav */}
        <header
          className="sticky top-0 z-50 h-16 flex items-center justify-between px-4 sm:px-6 backdrop-blur-xl"
          style={{ backgroundColor: "var(--user-nav-bg, #FFFFFF)", backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))", borderBottom: "1px solid var(--user-nav-border)" }}
          data-testid="learner-top-nav"
        >
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden transition-colors"
              style={{ color: "var(--user-nav-muted)" }}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="learner-mobile-menu"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <span className="md:hidden text-sm font-heading font-semibold" style={{ color: "var(--user-nav-text)" }} data-testid="learner-topnav-section-mobile">
              {sectionTitle}
            </span>
          </div>

          <span className="hidden md:block text-sm font-body" style={{ color: "var(--user-nav-muted)" }} data-testid="learner-topnav-section">
            {sectionTitle}
          </span>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm font-body" style={{ color: "var(--user-nav-muted)" }}>
              {fullName}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="learner-logout-button"
              className="hover:text-red-500 hover:bg-red-50"
              style={{ color: "var(--user-nav-muted)" }}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Logout</span>
            </Button>
          </div>
        </header>

        {/* Main */}
        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6" style={{ backgroundColor: "var(--user-bg, #F8F9FC)" }}>
          <BannerHost />
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="learner-mobile-overlay">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside
            className="absolute left-0 top-0 bottom-0 w-64 p-4 overflow-y-auto"
            style={{ backgroundColor: "var(--user-sidebar-bg, #FFFFFF)", backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))", borderRight: "1px solid var(--user-sidebar-border)" }}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="min-w-0 flex-1">
                <h1 className="font-heading text-base font-bold tracking-tight truncate leading-tight" style={{ color: "var(--user-sidebar-text)" }}>
                  {learner?.org_name || "Organization"}
                </h1>
                <span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "var(--user-sidebar-muted)", opacity: 0.7 }}>
                  Powered by DiNAXLP
                </span>
              </div>
              <button onClick={() => setSidebarOpen(false)} style={{ color: "var(--user-sidebar-muted)" }} className="ml-2">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="space-y-1">
              {visibleNav.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                      isActive ? "font-body font-bold" : "font-body font-medium"
                    }`
                  }
                  style={({ isActive }) => ({
                    color: "var(--shell-text)",
                    opacity: isActive ? 1 : 0.65,
                    borderLeft: isActive ? "3px solid var(--shell-section-label)" : "3px solid transparent",
                    paddingLeft: isActive ? "calc(0.75rem - 3px)" : "0.75rem",
                    paddingRight: "0.75rem",
                    backgroundColor: "transparent",
                  })}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Mobile Bottom Nav */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 backdrop-blur-lg z-40 flex justify-around py-2 px-1"
        style={{ backgroundColor: "var(--user-nav-bg, #FFFFFF)", backgroundImage: "linear-gradient(var(--shell-overlay, transparent), var(--shell-overlay, transparent))", borderTop: "1px solid var(--user-nav-border)" }}
        data-testid="learner-bottom-nav"
      >
        {visibleNav.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className="flex flex-col items-center gap-0.5 py-1 px-2 text-[10px] font-body rounded-lg transition-colors"
            style={({ isActive }) => ({
              color: "var(--shell-text)",
              opacity: isActive ? 1 : 0.65,
              fontWeight: isActive ? 700 : 500,
            })}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
